import { Badge, Button, Card, Center, Group, Loader, Stack, Table, Text, Title } from '@mantine/core';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useAnalysisTypes, type OutputDecl, type Slot, type Step } from '@/api/analyses';
import { errorMessage } from '@/api/errors';
import { EmptyState } from '@/components/EmptyState';
import { rowClick, rowStyle } from '@/components/rowClick';
import { usePageTitle } from '@/components/usePageTitle';
import { brand } from '@/theme/theme';

export const Route = createFileRoute('/_staff/staff/analysis-types/')({ component: AnalysisTypesPage });

function AnalysisTypesPage() {
  const { t, i18n } = useTranslation('analyses');
  usePageTitle(t('types.title'));
  const navigate = useNavigate();
  const types = useAnalysisTypes();
  const lang = i18n.language as 'cs' | 'en';
  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Title order={1}>{t('types.title')}</Title>
        <Button onClick={() => void navigate({ to: '/staff/analysis-types/$key', params: { key: 'new' } })}>{t('types.new')}</Button>
      </Group>
      {types.isPending ? (
        <Center py="xl">
          <Loader color="forest" />
        </Center>
      ) : types.isError ? (
        <EmptyState title={errorMessage(types.error)} />
      ) : (
        <Card p={0}>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('types.key')}</Table.Th>
                <Table.Th>{t('types.name')}</Table.Th>
                <Table.Th>{t('types.slots')}</Table.Th>
                <Table.Th>{t('types.steps')}</Table.Th>
                <Table.Th>{t('types.outputs')}</Table.Th>
                <Table.Th>{t('types.active')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {types.data.map((x) => (
                <Table.Tr
                  key={x.key}
                  style={{ ...rowStyle, ...(x.is_active ? {} : { opacity: 0.6 }) }}
                  onClick={rowClick(() => void navigate({ to: '/staff/analysis-types/$key', params: { key: x.key } }))}
                >
                  <Table.Td>
                    <Link to="/staff/analysis-types/$key" params={{ key: x.key }} style={{ fontWeight: 600, color: brand.text }}>
                      {x.key}
                    </Link>
                  </Table.Td>
                  <Table.Td>{(x.name as Record<string, string>)[lang] ?? ''}</Table.Td>
                  <Table.Td>
                    <Text fz={12} ff="monospace">
                      {(x.required_inputs as Slot[]).map((s) => `${s.role}:${s.kind}`).join(', ')}
                      {(x.optional_inputs as Slot[]).length ? ' · ' + (x.optional_inputs as Slot[]).map((s) => `${s.role}?`).join(', ') : ''}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text fz={12} ff="monospace">
                      {(x.run_sequence as Step[]).map((s) => s.run_type).join(' → ')}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text fz={12} ff="monospace">
                      {(x.outputs as OutputDecl[]).map((o) => o.kind + (o.deliverable ? '*' : '')).join(', ')}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge color={x.is_active ? 'forest' : 'gray'} variant="light">
                      {x.is_active ? t('types.yes') : t('types.no')}
                    </Badge>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Card>
      )}
    </Stack>
  );
}
